import { createContext, useCallback, useContext, useState } from 'react'

import type { StatusVariant } from '@factorialco/f0-react'
import type { MarketplaceIntegrationWithIdsFragment } from 'generated/resources/graphql'
import {
  CreateSyncRunDocument,
  CreateSyncRunMutation,
  ExpensablesDataCollectionQuery,
  IntegrationsAgreggatedSyncableStateValuesEnum,
  IntegrationsCapabilityModeEnum,
  IntegrationsSyncRunFragment,
  IntegrationsSyncRunOriginEnum,
  IntegrationsSyncableTypeEnum,
} from 'generated/resources/graphql'
import { print } from 'graphql'
import type { DocumentNode } from 'graphql'

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { GraphqlDataCollectionMappedVariables } from 'modules/core/hooks/useGraphqlDataCollectionSource'
import { useMutation } from 'modules/core/hooks/useMutation'
import { handleResult } from 'modules/core/lib/handleMutation'

import { toSyncParameters } from 'modules/integrations/lib/parameters'
import type { Parameters, SyncParameters } from 'modules/integrations/lib/parameters'
import { EmployeePayrollRunNode } from 'modules/compensations/components/EmployeePayrollRunElements'
import { SyncableStateAggregation } from 'modules/integrations/components/SyncFooter/LastSync/Syncing/StatusAggregation'
import { useAvailableIntegration } from 'modules/integrations/hooks/SyncContext/availableIntegration'
import { useSyncRunStatusChangedSubscription } from 'modules/integrations/hooks/SyncContext/subscriptions/useSyncRunStatusChangedSubscription'
import { useSyncableStateUpdatedSubscription } from 'modules/integrations/hooks/SyncContext/subscriptions/useSyncableStateUpdatedSubscription'
import {
  useReadLastSyncRun,
  useUpdateLastSyncRunQueryCache,
} from 'modules/integrations/hooks/SyncContext/useLastSyncRun'
import { setSyncRunStatusIdSessionStorage } from 'modules/integrations/lib/syncRun'
import { useSyncRunFileDownload } from 'modules/integrations/hooks/SyncContext/useSyncRunFileDownload'

type ExpensesExpensableNode =
  ExpensablesDataCollectionQuery['expenses']['expensablesConnection']['edges'][number]['node']

export type SyncStatusColumnItem = ExpensesExpensableNode | EmployeePayrollRunNode

// Accept any GraphqlDataCollectionSource variant regardless of type parameters
// since we only use mappedVariables which is common to all variants
export type AnyGraphqlDataCollectionSource = {
  mappedVariables?: GraphqlDataCollectionMappedVariables
  mappedBaseVariables?: GraphqlDataCollectionMappedVariables
  document?: DocumentNode
  [key: string]: unknown
}

export interface DataCollectionMappedVariable {
  key: string
  value: string
}

export type DataCollectionMappedVariables = DataCollectionMappedVariable[]

export interface Configuration {
  origin: IntegrationsSyncRunOriginEnum
  syncableType: IntegrationsSyncableTypeEnum
  isBookkeeperView?: boolean
  syncableStatesAggregation?: SyncableStateAggregation
  syncableStatusesFilter?: IntegrationsAgreggatedSyncableStateValuesEnum[]
  pollingEnabled?: boolean // When true, enables polling as a replacement for GraphQL subscriptions
  syncableEntityName: () => string // Returns human-readable name of the syncable entity (e.g. "payroll variables", "expenses", "compensations")
}

export type SyncStatusColumn = {
  label: string
  render: (item: SyncStatusColumnItem) => SyncStatusColumnValue | undefined
}

export type SyncStatusColumnValue = {
  type: 'status'
  value: {
    status: StatusVariant
    label: string
  }
}

export interface SyncContextType {
  isSyncing: boolean
  setContextParameters: (parameters: Parameters) => void
  setFilterParameters: (parameters: Parameters) => void // FIXME: It should be exposed only in the PrivateSyncContext when we manage filters from inside the framework
  setCompanyIdContext: (companyId: string) => void
  setCountry: (country: string | undefined) => void
  marketplaceIntegration: MarketplaceIntegrationWithIdsFragment | null
  syncEnabled: boolean
  lastSyncRun?: IntegrationsSyncRunFragment | null
  setDataCollectionSource: (source: AnyGraphqlDataCollectionSource) => void
  isExportMode: boolean
  hasSyncables: boolean
}

export interface PrivateSyncContextType {
  runSynchronisation: () => Promise<void>
  isSyncRunCreating: boolean
  isSyncing: boolean
  isLastSyncRunRunning: boolean
  contextParameters: Parameters
  mappedBaseVariables: GraphqlDataCollectionMappedVariables
  syncParameters: () => SyncParameters
  setFilterParameters: (parameters: Parameters) => void
  marketplaceIntegration: MarketplaceIntegrationWithIdsFragment | null
  syncEnabled: boolean
  lastSyncRun?: IntegrationsSyncRunFragment | null
  getDataCollectionSource: () => AnyGraphqlDataCollectionSource | null
  configuration: Configuration
  capabilityMode?: IntegrationsCapabilityModeEnum | null
  isBookkeeperView: boolean
  companyId: string | undefined
  hasSyncables: boolean
  setHasSyncables: (value: boolean) => void
}

const SyncContext = createContext<SyncContextType | null>(null)
const PrivateSyncContext = createContext<PrivateSyncContextType | null>(null)

let dataCollectionSource: AnyGraphqlDataCollectionSource | null = null

const setDataCollectionSource = (source: AnyGraphqlDataCollectionSource) => {
  dataCollectionSource = source
}

export const IntegrationsSyncContextProvider = ({
  children,
  configuration,
}: {
  children: React.ReactNode
  configuration: Configuration
}) => {
  const {
    company: { id: sessionCompanyId },
  } = useCurrentSession()
  const isBookkeeperView = configuration.isBookkeeperView ?? false
  const [contextParameters, setContextParameters] = useState<Parameters>({})
  const [companyIdContext, setCompanyIdContext] = useState<string | undefined>(
    isBookkeeperView ? undefined : sessionCompanyId
  )
  const [filterParameters, setFilterParameters] = useState<Parameters>({})
  const [hasSyncables, setHasSyncables] = useState(true)
  const [country, setCountry] = useState<string | undefined>(undefined)

  const availableIntegration = useAvailableIntegration(
    configuration.origin,
    isBookkeeperView,
    companyIdContext,
    country
  )

  const marketplaceIntegration = availableIntegration?.marketplaceIntegration ?? null
  const capabilityMode = availableIntegration?.capabilityMode ?? null

  const syncEnabled = !!marketplaceIntegration

  const updateLastSyncRunQueryCache = useUpdateLastSyncRunQueryCache()

  const syncParameters = useCallback(() => {
    return toSyncParameters(contextParameters).concat(toSyncParameters(filterParameters))
  }, [contextParameters, filterParameters])

  const [sync, { loading: isSyncRunCreating }] = useMutation(CreateSyncRunDocument, {
    extractorFn: (data: CreateSyncRunMutation) => {
      return data.integrationsMutations.createIntegrationsSyncRun
    },
    update(_, { data }) {
      const syncRun = data?.integrationsMutations?.createIntegrationsSyncRun?.syncRun
      if (!syncRun || !marketplaceIntegration) return

      // Update the LastSyncRun query cache with the new created sync run
      // instead refetching to avoid DBs replication lag issues
      updateLastSyncRunQueryCache({
        marketplaceIntegration,
        contextParameters,
        syncRun,
        origin: configuration.origin,
      })
    },
  })

  const pollingEnabled = configuration.pollingEnabled ?? false

  const skipSubscriptions = !syncEnabled || pollingEnabled

  useSyncRunStatusChangedSubscription({
    marketplaceIntegration,
    contextParameters,
    origin: configuration.origin,
    skip: skipSubscriptions,
  })

  useSyncableStateUpdatedSubscription({
    marketplaceIntegration,
    skip: skipSubscriptions,
  })

  const runSynchronisation = async () => {
    if (!syncEnabled || !marketplaceIntegration || !companyIdContext) return

    const mappedVariables = dataCollectionSource?.mappedVariables
    const queryForSync = dataCollectionSource?.document
      ? print(dataCollectionSource.document)
      : null

    const result = await sync({
      variables: {
        companyId: companyIdContext,
        marketplaceIntegrationId: marketplaceIntegration.id,
        origin: configuration.origin,
        parameters: syncParameters(),
        graphqlVariablesJson: queryForSync
          ? JSON.stringify({
              query: queryForSync,
              variables: { ...mappedVariables },
            })
          : undefined,
      },
      context: {
        isBookkeeperView,
      },
    })

    const handledResult = handleResult(result, () => {})
    if (handledResult.success) {
      const syncRunId = handledResult?.data?.syncRun?.id
      if (syncRunId) {
        setSyncRunStatusIdSessionStorage(syncRunId.toString())
      }
    }
  }

  const { data: lastSyncRunData, isLastSyncRunRunning } = useReadLastSyncRun({
    marketplaceIntegration,
    contextParameters,
    origin: configuration.origin,
    syncEnabled,
    isBookkeeperView,
    pollingEnabled,
  })

  const lastSyncRun = lastSyncRunData?.integrations?.syncRunsConnection.nodes[0] || null

  const { syncRunOutputsLoading } = useSyncRunFileDownload({
    isBookkeeperView,
    lastSyncRun,
  })

  const getDataCollectionSource = () => {
    return dataCollectionSource
  }

  const isSyncing = isSyncRunCreating || isLastSyncRunRunning || syncRunOutputsLoading
  const mappedBaseVariables = dataCollectionSource?.mappedBaseVariables

  return (
    <PrivateSyncContext.Provider
      value={{
        runSynchronisation,
        isSyncRunCreating,
        isSyncing,
        isLastSyncRunRunning,
        contextParameters,
        mappedBaseVariables,
        syncParameters,
        setFilterParameters,
        marketplaceIntegration,
        syncEnabled,
        lastSyncRun,
        getDataCollectionSource,
        configuration,
        capabilityMode,
        isBookkeeperView,
        companyId: companyIdContext,
        hasSyncables,
        setHasSyncables,
      }}
    >
      <SyncContext.Provider
        value={{
          isSyncing,
          setContextParameters,
          setFilterParameters,
          setCompanyIdContext,
          setCountry,
          marketplaceIntegration,
          syncEnabled,
          lastSyncRun,
          setDataCollectionSource,
          isExportMode: capabilityMode === 'file',
          hasSyncables,
        }}
      >
        {children}
      </SyncContext.Provider>
    </PrivateSyncContext.Provider>
  )
}

export function useIntegrationsSyncContext(): SyncContextType {
  const context = useContext(SyncContext)

  if (!context) {
    throw new Error('useIntegrationsSyncContext must be used within a SyncContextProvider')
  }

  return context
}

// As react hooks cannot be conditional, we need to use a flag to disable the context
export function usePrivateConditionalIntegrationsSyncContext(
  hidden = false
): PrivateSyncContextType | undefined {
  const context = useContext(PrivateSyncContext)
  if (hidden) return undefined

  if (!context) {
    throw new Error(
      'useConditionalIntegrationsSyncContext must be used within a SyncContextProvider'
    )
  }

  return context
}

export function usePrivateIntegrationsSyncContext() {
  const context = useContext(PrivateSyncContext)

  if (!context) {
    throw new Error(
      'usePrivateIntegrationsSyncContext must be used within a PrivateSyncContextProvider'
    )
  }

  return context
}
